import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Reveal from '@/components/feature/Reveal';

const FORM_URL = 'https://readdy.ai/api/form/dag9kj9tto0ceqqnhojg';

const needsOptions = [
  'Agentes IA',
  'Automatización',
  'Desarrollo de software',
  'Integración ERP',
  'SAP Business One',
  'Aplicación web',
  'Aplicación móvil',
  'SaaS',
  'Arquitectura',
  'Otro',
];

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const hp = String(formData.get('website_alt') ?? '').trim();
    if (hp) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt') {
        params.append(key, String(value));
      }
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (res.ok && parsed.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(
          parsed?.meta?.message || parsed?.meta?.detail || 'Ocurrió un error. Intenta nuevamente.'
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg('No pudimos enviar tu mensaje. Revisa tu conexión e intenta de nuevo.');
    }
  };

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main className="pt-28 md:pt-32 pb-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Left copy */}
            <div>
              <Reveal>
                <span className="inline-block text-xs md:text-sm font-semibold tracking-widest uppercase text-primary-400">
                  Contacto
                </span>
                <h1 className="mt-3 text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight tracking-tight">
                  Hablemos de tu proyecto.
                </h1>
                <p className="mt-5 text-base md:text-lg text-foreground-500 leading-relaxed">
                  Cuéntanos qué quieres resolver. Un equipo de ACOMA revisará tu
                  mensaje y te responderá para avanzar juntos.
                </p>
              </Reveal>

              <Reveal delay={120}>
                <div className="mt-10 space-y-5">
                  {[
                    { icon: 'ri-brain-line', t: 'Agentes IA e inteligencia artificial', d: 'Conecta la IA con los procesos reales de tu empresa.' },
                    { icon: 'ri-code-box-line', t: 'Software a medida', d: 'Plataformas web, móviles y SaaS desde cero.' },
                    { icon: 'ri-git-merge-line', t: 'Integraciones ERP', d: 'SAP Business One, APIs, pagos y sistemas externos.' },
                  ].map((item) => (
                    <div key={item.t} className="flex items-start gap-4">
                      <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-background-800 border border-background-700/60 text-primary-400">
                        <i className={`${item.icon} text-xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold font-heading text-foreground-950">
                          {item.t}
                        </h3>
                        <p className="mt-1 text-sm text-foreground-500">{item.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* Form */}
            <Reveal delay={100}>
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-lg border border-background-700/60 bg-background-900/50 p-7 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="nombre" className="block text-sm text-foreground-400 mb-2">
                      Nombre
                    </label>
                    <input
                      id="nombre"
                      name="nombre"
                      type="text"
                      required
                      placeholder="Tu nombre"
                      className="w-full text-sm bg-background-800/70 border border-background-700/60 rounded-md px-4 py-3 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="empresa" className="block text-sm text-foreground-400 mb-2">
                      Empresa
                    </label>
                    <input
                      id="empresa"
                      name="empresa"
                      type="text"
                      placeholder="Nombre de tu empresa"
                      className="w-full text-sm bg-background-800/70 border border-background-700/60 rounded-md px-4 py-3 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-400 mb-2">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="tucorreo@empresa.cl"
                      className="w-full text-sm bg-background-800/70 border border-background-700/60 rounded-md px-4 py-3 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="telefono" className="block text-sm text-foreground-400 mb-2">
                      Teléfono
                    </label>
                    <input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      placeholder="+56 9 ..."
                      className="w-full text-sm bg-background-800/70 border border-background-700/60 rounded-md px-4 py-3 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/60 transition-colors"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="necesidad" className="block text-sm text-foreground-400 mb-2">
                    ¿Qué necesitas?
                  </label>
                  <select
                    id="necesidad"
                    name="necesidad"
                    required
                    defaultValue=""
                    className="w-full text-sm bg-background-800/70 border border-background-700/60 rounded-md px-4 py-3 text-foreground-900 focus:outline-none focus:border-primary-500/60 transition-colors cursor-pointer"
                  >
                    <option value="" disabled>
                      Selecciona una opción
                    </option>
                    {needsOptions.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-5">
                  <label htmlFor="mensaje" className="block text-sm text-foreground-400 mb-2">
                    Cuéntanos sobre tu proyecto
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Describe qué quieres resolver..."
                    className="w-full text-sm bg-background-800/70 border border-background-700/60 rounded-md px-4 py-3 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/60 transition-colors resize-none"
                  />
                  <p className="mt-1 text-xs text-foreground-500">Máximo 500 caracteres.</p>
                </div>

                {/* Honeypot */}
                <div className="hp-field-wrap" aria-hidden="true">
                  <label htmlFor="website_alt">Sitio web</label>
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <div className="mt-5 flex items-start gap-3">
                  <input
                    id="consentimiento"
                    name="consentimiento"
                    type="checkbox"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-600 bg-background-800 text-primary-500 cursor-pointer"
                  />
                  <label htmlFor="consentimiento" className="text-sm text-foreground-500">
                    Acepto que ACOMA pueda contactarme para dar seguimiento a mi
                    consulta.
                  </label>
                </div>

                {status === 'error' && (
                  <p className="mt-4 text-sm text-red-400">{errorMsg}</p>
                )}
                {status === 'success' && (
                  <p className="mt-4 text-sm text-secondary-400">
                    ¡Gracias! Recibimos tu proyecto. Te contactaremos pronto.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="mt-6 w-full whitespace-nowrap font-medium text-background-950 bg-primary-500 hover:bg-primary-600 rounded-md px-6 py-3.5 transition-colors cursor-pointer glow-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? 'Enviando...' : 'Enviar proyecto'}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}